import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { formatFileSize } from '@/helpers/file-size';
import {
  isExcelFile,
  isImageFile,
  isPdfFile,
  isTextFile,
  isVideoFile,
  isWordFile,
} from '@/helpers/helpers';
import { FSR, FSRAttachment } from '@/Interface/fsrManagementType';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * Read-only view of a single FSR plus its activity log.
 *
 * Unlike the ticket detail page there is no chat panel, so no polling and no
 * visibilitychange listener either.
 */
@Component({
  selector: 'app-fsr-detail',
  templateUrl: './fsr-detail.component.html',
  styleUrls: ['./fsr-detail.component.scss'],
})
export class FsrDetailComponent implements OnInit, OnDestroy {
  statusBadgeMap: { [key: string]: string } = {
    unassigned: 'badge-warning',
    open: 'badge-cyan',
    assigned: 'badge-blue',
    'in progress': 'badge-blue',
    'on hold': 'badge-warning',
    'pending material': 'badge-warning',
    resolved: 'badge-success',
    closed: 'badge-gray',
    cancelled: 'badge-danger',
    reopened: 'badge-blue',
    approved: 'badge-success',
  };

  fsrId: number;
  fsrDetail: FSR | null = null;
  loader = true;

  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady = false;

  private routeSubscription: Subscription;

  readonly formatFileSize = formatFileSize;
  readonly isImageFile = isImageFile;
  readonly isVideoFile = isVideoFile;
  readonly isPdfFile = isPdfFile;

  constructor(
    private route: ActivatedRoute,
    private utilityService: UtilityService,
    private toastr: ToastrService,
    private router: Router,
    private permService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.initComponent();
  }

  async initComponent() {
    this.loader = true;
    try {
      await this.getComponentPermissions();
      if (!this.permissions.canView) {
        return;
      }
      this.routeSubscription = this.route.paramMap.subscribe((params) => {
        this.fsrId = +params.get('id')!;
        this.fsrDetail = null;
        this.loadDetail();
      });
    } finally {
      this.loader = false;
    }
  }

  async getComponentPermissions() {
    // The detail route is one segment deeper than its list route, so the parent
    // URL is the menu entry whose permissions it inherits.
    const url = this.permService.getParentUrl();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete,
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }

  private loadDetail(): void {
    this.loader = true;
    this.utilityService
      .getFsrDetail(this.fsrId)
      .pipe(finalize(() => (this.loader = false)))
      .subscribe({
        next: (response: FSR) => {
          this.fsrDetail = response;
          this.fsrDetail.attachments = (this.fsrDetail.attachments || []).map((file) => ({
            ...file,
            type: this.getAttachmentType(file),
          }));
        },
        error: () => {
          this.toastr.error('Could not load this FSR.');
        },
      });
  }

  getAttachmentType(file: FSRAttachment): string {
    if (isImageFile(file)) return 'image';
    if (isVideoFile(file)) return 'video';
    if (isPdfFile(file)) return 'pdf';
    if (isWordFile(file)) return 'word';
    if (isExcelFile(file)) return 'excel';
    if (isTextFile(file)) return 'text';
    return 'unknown';
  }

  normalizeStatus(status: string): string {
    return (status || '').toLowerCase().trim().replace(/\s+/g, ' ');
  }

  getStatusBadgeClass(status: string): string {
    if (!status) {
      return '';
    }
    return this.statusBadgeMap[this.normalizeStatus(status)] || 'badge-blue';
  }

  /** "City, Region" beneath the plant name, skipping whichever is missing. */
  get locationSubtitle(): string {
    return [this.fsrDetail?.plant_city, this.fsrDetail?.plant_region]
      .filter(Boolean)
      .join(', ');
  }

  /** The four checklist items, for rendering as a list of yes/no rows. */
  get checks(): { label: string; value: boolean }[] {
    if (!this.fsrDetail) {
      return [];
    }
    return [
      { label: 'Module Cleaning', value: this.fsrDetail.check_module_cleaning },
      { label: 'Loose Connections', value: this.fsrDetail.check_loose_connections },
      { label: 'Inverter Cleaning', value: this.fsrDetail.check_inverter_cleaning },
      { label: 'Module Damage', value: this.fsrDetail.check_module_damage },
    ];
  }

  /** Each panel group with its subtotal, for the breakdown table. */
  get panelGroupRows(): Array<{ number_of_panels: number; panel_capacity: string | number; subtotal: string }> {
    if (!this.fsrDetail?.panel_groups?.length) {
      return [];
    }
    return this.fsrDetail.panel_groups.map((group) => ({
      number_of_panels: group.number_of_panels,
      panel_capacity: group.panel_capacity,
      subtotal: (Number(group.number_of_panels) * Number(group.panel_capacity)).toFixed(3),
    }));
  }

  /** Only the replacements actually ticked, so the panel stays short. */
  get replacements(): string[] {
    if (!this.fsrDetail) {
      return [];
    }
    const items: string[] = [];
    if (this.fsrDetail.repl_broken_panel) items.push('Broken Panel');
    if (this.fsrDetail.repl_mc4) items.push('MC4');
    if (this.fsrDetail.repl_dc_cable) items.push('DC Cable');
    if (this.fsrDetail.repl_ac_cable) items.push('AC Cable');
    if (this.fsrDetail.repl_others) {
      items.push(`Others — ${this.fsrDetail.repl_others_detail || 'no detail'}`);
    }
    return items;
  }

  goBack(): void {
    this.router.navigate(['/help-desk/fsr']);
  }

  goToTicket(): void {
    if (this.fsrDetail?.ticket) {
      this.router.navigate(['/help-desk/tickets', this.fsrDetail.ticket]);
    }
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }
}
